import { useState, useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { Modal, ModalContent, ModalFooter, ModalBody, ModalHeader, useDisclosure, Button } from "@heroui/react";
import { UnitsTab } from "screens/buildings/components/factsTab/unitsTab";
import { BuildingInformation } from "screens/buildings/components/factsTab/buildingInformation.tsx";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { BuildingDTO } from "shared/types/building";
import { FinancialsTab } from "../components/financialsTab";
import OperationalCheckTable from "../components/operationalDashboard/operationalCheckTable";
import RentalTab from "../components/rentalTab/rentalTab";
import BuildingShellTab from "../components/buildingShellTab/buildingShellTab";
import { HistoryLog } from "../components/historyLog/historyLog";
import RenovationTab from "../components/renovationTab/renovationTab";
import { DropzoneTab } from "../components/dropzoneTab/dropzoneTab";
import { FolderStructureTab } from "../components/folderStructureTab/folderStructureTab";
import { ArrowUpRight } from "assets/icons";
import { QrCode, ArrowLeft } from "@phosphor-icons/react";
import { BuildingContactsTab } from "../components/buildingContactsTab/buildingContactsTab";
import ServiceTab from "../components/serviceTab/serviceTab";
import { QRCodeCanvas } from "qrcode.react";
import { BuildingAttributesTab } from "../components/buildingAttributesTab/buildingAttributesTab";
import { PlattegrondTab } from "../components/plattegrondTab/plattegrondTab";
import { InvestmentTab } from "../components/investmentTab/InvestmentTab";
import { DueDiligenceTab } from "../components/dueDiligenceTab";
import { BuildingAssetsTab } from "../components/buildingAssetsTab/buildingAssetsTab";

const Building = () => {
  const [building, setBuilding] = useState<BuildingDTO | null>(null);
  const {isOpen, onOpen, onClose} = useDisclosure();
  const { id } = useParams();
  const { session } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  async function fetchBuilding() {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/${id}?contracts=false`,
        { method: "GET" },
        session,
      );
      const buildingData = await response.json();
      setBuilding(buildingData);
    } catch (error) {
      console.error("Error fetching building:", error);
    } finally {
    }
  }
  useEffect(() => {
    setBuilding(null);
    fetchBuilding();
  }, [id]);

  const handleGenerateQRCode = () => {
    onOpen();
  };

  // Determine which content to render based on current route
  const renderContent = () => {
    const pathParts = location.pathname.split('/');
    const tab = pathParts[3]; // /building/:id/[tab]
    
    // Map routes to content
    switch (tab) {
      case "units":
        return <UnitsTab buildingId={building?.id as string} />;
      case "verhuur":
        return <RentalTab building={building as BuildingDTO} refreshBuilding={fetchBuilding} />;
      case "financieel":
        return (
          <FinancialsTab
            buildingId={building?.id as string}
            acquisitionDate={(building?.acquisitionDate as any) || ""}
            buildingIsOwned={building?.owner || false}
          />
        );
      case "due-diligence":
        return (
          <OperationalCheckTable
            building={building as BuildingDTO}
            refreshBuilding={fetchBuilding}
          />
        );
      case "due-diligence-nieuw":
        return (
          <DueDiligenceTab
            building={building as BuildingDTO}
            refreshBuilding={fetchBuilding}
          />
        );
      case "onderhoud":
        return <ServiceTab building={building as BuildingDTO} />;
      case "renovatie":
      case "renovation": // Legacy route support
        return <RenovationTab building={building as BuildingDTO} />;
      case "bouwschil":
        return <BuildingShellTab building={building as BuildingDTO} />;
      case "contacts":
        return <BuildingContactsTab building={building as BuildingDTO} />;
      case "attributen":
        return <BuildingAttributesTab building={building as BuildingDTO} />;
      case "plattegrond":
        return <PlattegrondTab building={building as BuildingDTO} />;
      case "investeringen":
        return <InvestmentTab building={building as BuildingDTO} />;
      case "voorzieningen":
        return <BuildingAssetsTab buildingId={building?.id as string} />;
      case "historiek":
        return <HistoryLog buildingId={building?.id as string} />;
      case "dropzone":
        return <DropzoneTab buildingId={building?.id as string} />;
      case "mappenstructuur":
        return <FolderStructureTab buildingId={building?.id as string} />;
      default:
        // Default to overview/gegevens
        return (
          <BuildingInformation
            buildingData={building as BuildingDTO}
            refreshBuilding={fetchBuilding}
          />
        );
    }
  };
  
  if (!building) return <div></div>;

  return (
    building && (
      <>
      <div className="z-10 mb-10 flex flex-col rounded-lg bg-white p-4 shadow-md">
        <header className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Terug"
              className="text-neutral-secondary-mid hover:text-brick-primary inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-neutral-primary/5 transition-colors"
            >
              <ArrowLeft size={20} weight="bold" />
            </button>
            <p className="text-sm font-medium uppercase text-neutral-secondary-mid">
              {building?.id}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="light"
              className="text-sm font-medium text-moss-primary"
              onClick={handleGenerateQRCode}
            >
              <QrCode className="h-[18px] w-[18px]" />
              Genereer QR Code
            </Button>
            
            {(building as any)?.sharePointFolderUrl && (
              <Link
                to={building?.sharePointFolderUrl as string}
                className="flex items-center gap-0.5 text-sm font-medium text-moss-primary"
                target="_blank"
              >
                Documenten <ArrowUpRight className="h-[18px] w-[18px]" />
              </Link>
            )}
            {(building as any)?.acquisitionFile && (
              <Link
                to={`/acquisitions/${
                  (building as any)?.acquisitionFile?.id
                }/detail`}
                className="flex items-center gap-0.5 text-sm font-medium text-moss-primary"
              >
                Acquisitiefiche <ArrowUpRight className="h-[18px] w-[18px]" />
              </Link>
            )}
          </div>
        </header>
        
        {/* NavMenu automatically shows subnavigation based on routes.tsx */}
        {renderContent()}
      </div>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          <ModalHeader>QR Code voor {building?.id}</ModalHeader>
          <ModalBody className="flex items-center justify-center">
            <div className="p-4 flex flex-col items-center">
              <QRCodeCanvas 
                value={`https://buildings.yally.be/${building?.id}/hello`}
                size={250}
              />
              <p className="mt-4 text-sm text-neutral-secondary-mid">
                Scan deze QR code om naar {building?.id} te gaan
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="default" variant="light" onPress={onClose}>
              Sluiten
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
    )
  );
};

export default Building;
